import { Autocomplete, AutocompleteItem } from '@heroui/react';
import cn from 'classnames';
import type { CountryItem } from './types';
import { useCountryAutocomplete } from './useCountryAutocomplete';

export type CountryAutocompleteProps = {
  countryItems: CountryItem[];
  currentIso2: string;
  setCountry: (iso2: string) => void;
  label?: string;
  hasLabel: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  ariaLabel: string;
  className?: string;
  triggerClassName?: string;
};

export const CountryAutocomplete = ({
  countryItems,
  currentIso2,
  setCountry,
  label,
  hasLabel,
  isInvalid,
  isDisabled,
  ariaLabel,
  className,
  triggerClassName,
}: CountryAutocompleteProps) => {
  const { countryInputValue, filteredCountryItems, handleCountrySelect, handleInputChange, handleClose } =
    useCountryAutocomplete(countryItems, currentIso2, setCountry);

  return (
    <Autocomplete
      placeholder="Search"
      className={cn('max-w-[7rem]', className)}
      value={currentIso2}
      inputValue={countryInputValue}
      onInputChange={handleInputChange}
      onChange={(key: React.Key | null) => {
        handleCountrySelect(key);
      }}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose();
        }
      }}
      items={filteredCountryItems}
      label={label}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      aria-label={ariaLabel}
      isClearable={false}
      isVirtualized={false}
      menuTrigger="input"
      classNames={{
        base: cn(isInvalid && 'tmdx-select--invalid', 'shadow-none'),
        popoverContent: 'shadow-xl bg-base-60 rounded-lg p-0 min-w-72',
        listboxWrapper: 'p-0',
        listbox: 'p-0',
      }}
      inputProps={{
        classNames: {
          inputWrapper: cn(
            'bg-base-60 data-[focus=true]:bg-base-40 group-data-[invalid=true]:bg-base-60! group-data-[invalid=true]:group-data-[hover=true]:bg-base-60! rounded-lg transition-none! shadow-none',
            !hasLabel && 'min-h-8 h-8',
            hasLabel && 'hasLabel',
            triggerClassName,
          ),
          input: ['text-base-5', 'text-m-r!', 'group-data-[invalid=true]:text-base-5!'],
          label: ['text-base-20', 'text-sm', 'group-focus-within:text-base-10', 'data-[invalid=true]:text-danger!'],
        },
      }}
      listboxProps={{
        itemClasses: {
          base: 'px-3 py-2 rounded-lg data-[hover=true]:bg-base-50! data-[selected=true]:bg-base-50!',
        },
      }}
      popoverProps={{ classNames: { base: 'p-0' } }}
      selectorButtonProps={{ disableRipple: true, className: 'data-[hover=true]:bg-transparent' }}
    >
      {(item) => (
        <AutocompleteItem key={item.iso2} textValue={`${item.flag} +${item.dialCode}`}>
          <span className="flex items-center gap-1 text-base-5">
            <span className="text-xl">{item.flag}</span>
            {` ${item.name} `}
            <span className="text-base-25">{`+${item.dialCode}`}</span>
          </span>
        </AutocompleteItem>
      )}
    </Autocomplete>
  );
};
